import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  Assessment,
  AssessmentName,
  OntMode,
  ProductInstanceStatus,
  UpdateCommandError,
  UpdateServiceSubTypeInput,
  UpdateServiceSubTypePayload,
} from '@mfe/shared/schema-types';

import { FetchWithErrorsQuery } from '../graphql';

const HOME_NETWORK_ASSESSMENT_NAMES = [
  AssessmentName.DishAlignment,
  AssessmentName.DishToModemConnection,
  AssessmentName.WifiModem,
];

const VIASAT_NETWORK_ASSESSMENT_NAMES = [AssessmentName.ViasatNetwork, AssessmentName.CurrentWeatherStatus];

type NetworkState = {
  loading: boolean;
  error: FetchWithErrorsQuery['errors'] | null;
  diagnosticData: Assessment[] | null;
  runDate: string | null;
  isRebootModemModalOpen: boolean;
  isModemOnline: boolean;
  ssidLoading: boolean;
  ssidUpdateLoading: boolean;
  ssidName: string;
  newSsidPassword: string | null;
  hasUpdatedSsidName: boolean;
  hasUpdatedSsidPassword: boolean;
  updateSuccess?: boolean;
  ssidError: FetchWithErrorsQuery['errors'] | null;
  ssidRateLimitError: boolean;
  ontModeLoading: boolean;
  ontModeError: FetchWithErrorsQuery['errors'] | null;
  ontMode: OntMode | null;
  ontModeSwitchLoading: boolean;
  updateStatus: string | undefined | null;
  updateError: UpdateCommandError | undefined | null;
};

export const initialNetworkState: NetworkState = {
  loading: false,
  error: null,
  diagnosticData: null,
  runDate: null,
  isRebootModemModalOpen: false,
  isModemOnline: true,
  ssidLoading: false,
  ssidUpdateLoading: false,
  ssidName: '',
  newSsidPassword: null,
  hasUpdatedSsidName: false,
  hasUpdatedSsidPassword: false,
  updateSuccess: false,
  ssidError: null,
  ssidRateLimitError: false,
  ontModeLoading: false,
  ontModeError: null,
  ontMode: null,
  ontModeSwitchLoading: false,
  updateStatus: null,
  updateError: null,
};

export type UpdateSsidInfoAction = PayloadAction<{
  passphrase: string;
  ssid?: string;
}>;

type CanFetchFiberServiceSubTypeParams = {
  enableFiber: boolean;
  enableBridgeMode: boolean;
  hasFiber: boolean;
  productInstanceStatus: ProductInstanceStatus | null | undefined;
};

export const canFetchFiberServiceSubType = ({
  enableFiber,
  enableBridgeMode,
  hasFiber,
  productInstanceStatus,
}: CanFetchFiberServiceSubTypeParams): boolean =>
  enableFiber && enableBridgeMode && hasFiber && productInstanceStatus === ProductInstanceStatus.Active;

export const networkSlice = createSlice({
  name: 'network',
  initialState: initialNetworkState,
  reducers: {
    getSsidName: (state) => {
      state.ssidLoading = true;
    },
    setSsidName: (state, { payload }: { payload: string }) => {
      state.ssidLoading = false;
      state.ssidName = payload;
    },
    updateSsidInfo: (state, action: UpdateSsidInfoAction) => {
      state.ssidUpdateLoading = true;
      state.ssidRateLimitError = false;
    },
    setSsidError: (state, { payload }) => {
      state.ssidError = payload;
      state.ssidLoading = false;
    },
    setSsidRateLimitError: (state, { payload }: { payload: boolean }) => {
      state.ssidRateLimitError = payload;
      state.ssidUpdateLoading = false;
    },
    setNewSsidPassword: (state, { payload }: { payload: string }) => {
      state.newSsidPassword = payload;
    },
    setHasUpdatedSsidName: (state, { payload }: { payload: boolean }) => {
      state.ssidUpdateLoading = false;
      state.hasUpdatedSsidName = payload;
    },
    setHasUpdatedSsidPassword: (state, { payload }: { payload: boolean }) => {
      state.ssidUpdateLoading = false;
      state.hasUpdatedSsidPassword = payload;
    },
    setUpdateSSIDSuccess: (state) => {
      state.hasUpdatedSsidName = false;
      state.hasUpdatedSsidPassword = false;
      state.updateSuccess = true;
    },
    resetUpdateSSIDSuccess: (state) => {
      state.updateSuccess = false;
    },
    runDiagnostic: (state) => {
      state.loading = true;
      state.error = null;
      state.diagnosticData = null;
    },
    setDiagnostic: (state, { payload }: { payload: Assessment[] }) => {
      state.loading = false;
      state.error = null;
      state.diagnosticData = payload;
    },
    resetDiagnosticData: (state) => {
      state.diagnosticData = null;
    },
    setNetworkError: (state, { payload }) => {
      state.ssidUpdateLoading = false;
      state.loading = false;
      state.error = payload;
    },
    setRunDate: (state, { payload }) => {
      state.runDate = payload;
    },
    setIsRebootModemModalOpen: (state, { payload }) => {
      state.isRebootModemModalOpen = payload;
    },
    setModemStatus: (state, { payload }: { payload: boolean }) => {
      state.isModemOnline = payload;
    },
    getFiberServiceSubType: (state) => {
      state.ontModeLoading = true;
      state.ontModeError = null;
    },
    resetFiberServiceSubType: (state) => {
      state.ontModeLoading = false;
      state.ontModeError = null;
      state.ontMode = null;
    },
    setFiberServiceSubType: (state, { payload }: { payload: OntMode }) => {
      state.ontModeLoading = false;
      state.ontModeError = null;
      state.ontMode = payload;
      state.ontModeSwitchLoading = false;
    },
    setFiberServiceSubTypeError: (state, { payload }) => {
      state.ontModeLoading = false;
      state.ontModeError = payload;
      state.ontMode = null;
      state.ontModeSwitchLoading = false;
    },
    updateServiceSubType: (state, { payload }: { payload: UpdateServiceSubTypeInput }) => {
      state.ontModeSwitchLoading = true;
    },
    setSubTypeUpdateStatus: (state, { payload }: { payload: UpdateServiceSubTypePayload }) => {
      state.updateStatus = payload.status;
      state.updateError = payload.error;
    },
    resetSubTypeUpdateStatus: (state) => {
      state.updateStatus = null;
      state.updateError = null;
    },
    resetModemIssue: (state) => {
      const updateDiagnosticData = state.diagnosticData?.map((assessment) => {
        if (assessment.name === AssessmentName.WifiModem) {
          return { ...assessment, isOK: true };
        }
        return assessment;
      });
      if (updateDiagnosticData) {
        state.diagnosticData = updateDiagnosticData;
      }
    },
  },
});

export const {
  runDiagnostic,
  setDiagnostic,
  setRunDate,
  setNetworkError,
  resetDiagnosticData,
  setIsRebootModemModalOpen,
  resetModemIssue,
  setModemStatus,
  getSsidName,
  setSsidName,
  updateSsidInfo,
  setNewSsidPassword,
  setHasUpdatedSsidName,
  setHasUpdatedSsidPassword,
  setUpdateSSIDSuccess,
  resetUpdateSSIDSuccess,
  setSsidError,
  setSsidRateLimitError,
  getFiberServiceSubType,
  resetFiberServiceSubType,
  setFiberServiceSubType,
  setFiberServiceSubTypeError,
  updateServiceSubType,
  setSubTypeUpdateStatus,
  resetSubTypeUpdateStatus,
} = networkSlice.actions;

export const selectSsid = (state: { network: NetworkState }) => ({
  ssidLoading: state.network.ssidLoading,
  ssidUpdateLoading: state.network.ssidUpdateLoading,
  ssidName: state.network.ssidName,
  newSsidPassword: state.network.newSsidPassword,
  hasUpdatedSsidName: state.network.hasUpdatedSsidName,
  hasUpdatedSsidPassword: state.network.hasUpdatedSsidPassword,
  updateSuccess: state.network.updateSuccess,
  error: state.network.error,
  ssidError: state.network.ssidError,
  ssidRateLimitError: state.network.ssidRateLimitError,
});

export const selectNetwork = (state: { network: NetworkState }) => state.network;

export const selectNetworkIssues = (state: { network: NetworkState }) => {
  const diagnosticData = state.network.diagnosticData;
  if (!diagnosticData) {
    return null;
  }
  const diagnosticIssues = diagnosticData.filter((diagnostic) => diagnostic.isOK === false);

  if (!diagnosticIssues.length) {
    return null;
  }
  return diagnosticIssues;
};

export const selectDiagnostics = (state: { network: NetworkState }) => {
  const diagnosticData = state.network.diagnosticData;
  const runDate = state.network.runDate;
  if (!diagnosticData) {
    return { viasatAssessments: null, homeAssessments: null, runDate };
  }

  const viasatAssessments: Assessment[] = [],
    diagnosticIssues: Assessment[] = [],
    homeAssessments: Assessment[] = [];

  diagnosticData.forEach((assessment) => {
    if (VIASAT_NETWORK_ASSESSMENT_NAMES.includes(assessment.name)) {
      viasatAssessments.push(assessment);
    }
    if (HOME_NETWORK_ASSESSMENT_NAMES.includes(assessment.name)) {
      homeAssessments.push(assessment);
    }
    if (!assessment.isOK) {
      diagnosticIssues.push(assessment);
    }
  });

  return { viasatAssessments, homeAssessments, diagnosticIssues, runDate };
};
